'use client';

import { ScrollTopButton } from '@packages/modules/src/ScrollTopButton/ScrollTopButton';
import { useIntersectionObserver } from '@packages/shared/src/hooks/useIntersectionObserver/useIntersectionObserver';
import React, { useEffect, useRef, useState } from 'react';

export type StickyAreaProps = {
  /**
   * on mobile always visible content
   * */
  visibleContent: React.ReactElement;
  /**
   * on mobile invisible content -> will be visible if Image Gallery will leave viewport
   * */
  invisibleContent: React.ReactElement;
  /**
   * a string that represents the id of the reference element
   * usually this is the id of the image gallery ('image-gallery')
   * */
  referenceID: string;
};

/**
 * This component is fixed at the bottom of the viewport.
 *
 * The `StickyArea` uses the `ImageGallery` as ref
 *
 * - on **mobile** and **tablet** the "invisbleContent" will transition in, if the `ImageGallery` is not in the viewport
 * - on **desktop** the whole `StickyArea` is **only visible** if the `ImageGallery` is out of viewport
 * */
export const StickyArea = ({ visibleContent, invisibleContent, referenceID }: StickyAreaProps) => {
  const attachedNodeRef = useRef<HTMLElement>(null);

  useEffect(() => {
    attachedNodeRef.current = document.getElementById(referenceID);
  }, [referenceID]);

  // the state is needed -> without it you have an animation at initial render
  // Is intialized with true, because the StickyArea should not transition in directly after it is mounted.
  const [isIntersecting, setIsIntersecting] = useState(true);
  useIntersectionObserver(attachedNodeRef, {
    callback: (isInview) => setIsIntersecting(!!isInview),
    intersectOnce: false,
  });

  return (
    <aside
      className={`bg-color-background-base sticky bottom-0 z-50 w-full p-1 pt-1.5 shadow-lg lg:translate-y-full lg:p-2 lg:px-0 lg:opacity-0 lg:transition-all lg:duration-700 lg:[&.show]:translate-y-0 lg:[&.show]:opacity-100 ${!isIntersecting ? 'show' : 'hide'}`}
    >
      <section className="mx-auto w-full max-w-screen-xl lg:flex lg:flex-row lg:items-center lg:justify-between lg:gap-1 lg:px-1">
        <article className="max-h-0 overflow-hidden opacity-0 transition-all duration-700 will-change-contents lg:max-h-max lg:w-[65%] lg:opacity-100 [.show_&]:max-h-[200px] [.show_&]:opacity-100">
          <div className="pb-1.5 lg:pb-0">{invisibleContent}</div>
        </article>
        <article className="lg:w-[35%]">{visibleContent}</article>
      </section>
      <div className="absolute -top-2 right-1 -translate-y-full">
        <ScrollTopButton />
      </div>
    </aside>
  );
};
